<script setup lang="ts">
import { computed } from 'vue'
import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogOverlay,
    DialogPortal,
    useForwardPropsEmits,
} from 'radix-vue'
import { type CloseButtonVariants, type SheetVariants, closeButtonVariants, sheetVariants } from '.'
import { cn } from '@/lib/utils'

interface SheetContentProps extends DialogContentProps {
    class?: string
    side?: SheetVariants['side']
    theme?: CloseButtonVariants['theme']
    closeButton?: boolean
}

defineOptions({
    inheritAttrs: false,
})

const props = withDefaults(defineProps<SheetContentProps>(), {
    closeButton: true,
})

const emits = defineEmits<DialogContentEmits>()

const delegatedProps = computed(() => {
    const { class: _, side, ...delegated } = props

    return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
    <DialogPortal>
        <DialogOverlay
            class="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:lp-fade-out-0 data-[state=open]:lp-fade-in-0 fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        />
        <DialogContent
            :class="cn(sheetVariants({ side }), props.class)"
            v-bind="{ ...forwarded, ...$attrs }"
        >
            <slot />

            <DialogClose
                v-if="closeButton"
                :class="cn(closeButtonVariants({ theme }))"
            >
                <svg
                    class="inline fill-inherit align-baseline transition-transform duration-300 ease-in-out group-hover:scale-[1.333] group-active:scale-100"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.292893 0.292894C0.683417 -0.0976304 1.31658 -0.0976304 1.70711 0.292894L11.7071 10.2929C12.0976 10.6834 12.0976 11.3166 11.7071 11.7071C11.3166 12.0976 10.6834 12.0976 10.2929 11.7071L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683418 0.292893 0.292894Z"
                    />
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.292893 11.7071C-0.0976311 11.3166 -0.0976311 10.6834 0.292893 10.2929L10.2929 0.292894C10.6834 -0.0976312 11.3166 -0.0976312 11.7071 0.292894C12.0976 0.683417 12.0976 1.31658 11.7071 1.70711L1.70711 11.7071C1.31658 12.0976 0.683418 12.0976 0.292893 11.7071Z"
                    />
                </svg>
            </DialogClose>
        </DialogContent>
    </DialogPortal>
</template>
